import React, { useMemo, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Form, Input, Popconfirm, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import DF from 'DisplayFramework';
import { indexBy } from 'utils/utils2';
import Button from 'components/AddButtonSubEntity';
import SaveButton from 'components/SaveButtonAction';
import DataGrid from 'components/DataGrid/DataGrid';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import {
  ParAssetCatalogLabel,
  getAssetCatalogLabelList,
  addAssetCatalogLabel,
  updateAssetCatalogLabel,
  removeAssetCatalogLabel,
  AssetCatalogResponse,
} from 'services/assetconfig';
import { useFormList } from 'hooks/useFormList';
import DeleteButton from 'components/DeleteButton';
import { isNil } from 'lodash';

const NEW_CATALOG_LABEL_ID = -1;

type AssetCatalogLabelsTableProps = {
  asset: AssetCatalogResponse;
  isModal?: string;
};

const AssetCatalogLabelsTable = (props: AssetCatalogLabelsTableProps) => {
  const { catalogid } = props.asset;
  const { isModal } = props;
  const [form] = Form.useForm();
  const fieldName = 'labels';

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'labelid',
    addAsFirst: true,
  });
  const [loadingBtns, setLoadingBtns] = useState<boolean>(false);
  const { data, refetch,isFetching } = useQuery(
    ['getAssetCatalogLabelList', catalogid],
    () => getAssetCatalogLabelList({ catalogid }).then((res) => res.list || []),
    { refetchOnMount: false, enabled: catalogid !== undefined }
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data,
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data]);

  const addRow = () => {
    const row: ParAssetCatalogLabel = {
      labelid: NEW_CATALOG_LABEL_ID,
      catalogid,
      lancode: '',
      assetlabel: '',
      assettaglabel: '',
      assetdescription: '',
    };
    add(row);
  };

  const onSave = 
    (record: ParAssetCatalogLabel) => {
     setLoadingBtns(true)
      const steps: ParAssetCatalogLabel[] = form.getFieldValue(fieldName);
      const index = indexBy(steps, 'labelid')[record.labelid];
      const currentValue = steps[index];
      const { labelid } = currentValue;
      labelid === NEW_CATALOG_LABEL_ID
        ? 
        addAssetCatalogLabel({
            req: { ...currentValue },
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
              setLoadingBtns(false);

            }
          }).catch(() => {
            setLoadingBtns(false);
          })
        : updateAssetCatalogLabel({ req: currentValue }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
              setLoadingBtns(false);
            }
            
          }).catch(() => {
            setLoadingBtns(false);
          });
          

    }
  const columns = useMemo<DataGridColDef<ParAssetCatalogLabel>[]>(
    () => [
      // {
      //   dataIndex: 'labelid',
      //   width: 100,
      //   render: (_value, _record, index) => (
      //     <Form.Item
      //       name={[
      //         fieldName,
      //         indexBy(form.getFieldValue(fieldName), 'labelid')[
      //           _record.labelid
      //         ],
      //         'labelid',
      //       ]}
      //     >
      //       {_value}
      //     </Form.Item>
      //   ),
      // },
      {
        dataIndex: 'lancode',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'labelid')[
                _record.labelid
              ],
              'lancode',
            ]}
          >
            <ParListSelect parcode="LANCODE" />
          </Form.Item>
        ),
        width: 180,
      },
      {
        dataIndex: 'assetlabel',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'labelid')[
                _record.labelid
              ],
              'assetlabel',
            ]}
          >
            <Input />
          </Form.Item>
        ),
        width: 200,
      },
      {
        dataIndex: 'assetdescription',
        render: (_value, _record, index) => {
          return (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'labelid')[
                  _record.labelid
                ],
                'assetdescription',
              ]}
            >
              <Input />
            </Form.Item>
          );
        },
        width: 240,
      },
      {
        dataIndex: 'assettaglabel',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'labelid')[
                _record.labelid
              ],
              'assettaglabel',
            ]}
          >
            <Input />
          </Form.Item>
        ),
        width: 180,
      },
      {
        key: 'actions',
        render: (_, _record) =>
          _record.labelid === NEW_CATALOG_LABEL_ID ? (
            <>
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(_record);
                  setLoadingBtns(true)
                }}
                size="small"
                comcode="save"
                loading={loadingBtns||isFetching}
              />
              <DeleteButton onlyIcon onClick={() => remove(_record.labelid)} />
            </>
          ) : (
            <>
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(_record);
                  setLoadingBtns(true)
                }}
                size="small"
                comcode="update"
                loading={loadingBtns}
              />
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  removeAssetCatalogLabel({
                    labelid: _record.labelid,
                    catalogid: _record.catalogid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ),
        width: 150,
        fixed: 'right',
      },
    ],
    [form, onSave, refetch, remove,loadingBtns]
  );

  return (
    <DF.Provider pagcode="assetLabels">
      <Form form={form} initialValues={INITIAL_VALUES}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const labels: ParAssetCatalogLabel[] = getFieldValue(fieldName);

            const isNewItemInTable =
              labels?.find(
                ({ labelid }) => labelid === NEW_CATALOG_LABEL_ID
              ) !== undefined;

            return (
              <DataGrid
                rowKey="labelid"
                columns={columns}
                dataSource={labels}
                titleComcode="assetCatalogLabels"
                hideFilters
                extraActions={
                  <Button
                    type="primary"
                    disabled={isNewItemInTable}
                    icon={<PlusOutlined />}
                    onClick={addRow}
                    comcode="add"
                    hidden={isNil(catalogid) || !!isModal}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

const INITIAL_VALUES = {
  labels: [],
};

export default AssetCatalogLabelsTable;
